import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { PageLayout } from '@client/components/_core/layout/PageLayout';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { DatePicker } from '@client/components/_ui/data-entry/DatePicker';
import { Markdown } from '@client/components/_ui/display/Markdown';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { PdsAnalysisTab } from '@client/components/market-pds/PdsAnalysisTab';
import { SmartPdsUploadModal } from '@client/components/market-pds/SmartPdsUploadModal';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterOutputs } from '@client/trpc/client';
import type { ShareableRouteContext } from '@client/types/router';
import { createFileRoute } from '@tanstack/react-router';
import { Alert, Card, Descriptions, Empty, Form, Input, InputNumber, Select, Skeleton, Table, Tabs, Tag } from 'antd';
import { FileText, RefreshCw, Replace } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// Route definition
// ---------------------------------------------------------------------------

export const Route = createFileRoute('/console/$orgId/tools/pds-compare/pds-documents/edit/$id')({
    params: {
        parse({ id }) {
            return { id };
        },
    },
    staticData: {
        presentation: 'fullPage',
        ui: {
            icon: FileText,
            widthVariant: 'default',
        },
    },
    beforeLoad({ context: { trpcUtils }, params }): ShareableRouteContext {
        async function title() {
            const pds = await trpcUtils.marketPds.getOrgPds.ensureData({ id: BigInt(params.id) });
            const name = pds?.name || 'Edit PDS';
            return (
                <span className="inline-block max-w-md truncate align-bottom" title={name}>
                    {name}
                </span>
            );
        }
        return {
            breadcrumb: { title: 'Edit PDS' },
            ui: { title, subtitle: 'View and edit PDS details' },
        };
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <EditOrgPds />
        </PageController>
    );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type OrgPdsData = RouterOutputs['marketPds']['getOrgPds'];
type OrgPdsSection = OrgPdsData['Sections'][number];

type EditFormValues = {
    name: string;
    insurerName: string;
    category: string;
    year: number;
    version: string | null;
    effectiveDate: Date | null;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<string, string> = {
    DRAFT: 'default',
    PROCESSING: 'blue',
    PUBLISHED: 'green',
    ARCHIVED: 'orange',
};

const SECTION_TYPE_COLORS: Record<string, string> = {
    COVERAGE: 'green',
    EXCLUSION: 'red',
    CONDITION: 'blue',
    LIMIT: 'purple',
    DEFINITION: 'cyan',
    PROCEDURE: 'orange',
    GENERAL: 'default',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function EditOrgPds() {
    const { id } = Route.useParams();
    const { trpc, trpcUtils } = useTrpc();
    const { formatDateTime } = useFormatter();
    const formId = useId();
    const [form] = Form.useForm<EditFormValues>();
    const [replaceFileOpen, setReplaceFileOpen] = useState(false);
    const [categorySearch, setCategorySearch] = useState('');
    const currentCategory = Form.useWatch('category', form);

    const pdsId = BigInt(id);

    const {
        data: orgPds,
        isLoading,
        error,
    } = trpc.marketPds.getOrgPds.useQuery(
        { id: pdsId },
        {
            refetchOnWindowFocus: false,
            refetchInterval: (query) => {
                const status = query.state.data?.status;
                return status === 'PROCESSING' ? 5000 : false;
            },
        },
    );

    const { data: categories } = trpc.marketPds.listCategories.useQuery();
    const { data: insurers } = trpc.marketPds.listInsurers.useQuery();

    const { mutate: updateMutate, isPending: isSaving } = trpc.marketPds.updateOrgPds.useMutation({
        onError: (err) => errorMessage.show(err),
    });

    const { mutate: reprocessMutate } = trpc.marketPds.reprocessOrgPds.useMutation({
        onError: (err) => errorMessage.show(err),
    });

    // Populate form when data loads
    useEffect(() => {
        if (!orgPds) return;
        form.setFieldsValue({
            name: orgPds.name,
            insurerName: orgPds.insurerName,
            category: orgPds.category,
            year: orgPds.year,
            version: orgPds.version,
            effectiveDate: orgPds.effectiveDate,
        });
    }, [orgPds?.id, form]);

    const handleSave = (values: EditFormValues) => {
        const key = 'update_pds';
        message.loading({ content: 'Saving...', key });
        updateMutate(
            {
                id: pdsId,
                name: values.name,
                insurerName: values.insurerName,
                category: values.category,
                year: values.year,
                version: values.version ?? null,
                effectiveDate: values.effectiveDate ?? null,
            },
            {
                onSuccess: () => {
                    message.success({ content: 'PDS updated', key });
                    trpcUtils.marketPds.getOrgPds.invalidate({ id: pdsId });
                    trpcUtils.marketPds.listOrgPds.invalidate();
                    trpcUtils.marketPds.listCategories.invalidate();
                    trpcUtils.marketPds.listInsurers.invalidate();
                },
            },
        );
    };

    const handleReprocess = () => {
        const key = 'reprocess';
        message.loading({ content: 'Queuing reprocessing...', key });
        reprocessMutate(
            { id: pdsId },
            {
                onSuccess: () => {
                    message.success({ content: 'Reprocessing queued', key });
                    trpcUtils.marketPds.getOrgPds.invalidate({ id: pdsId });
                    trpcUtils.marketPds.listOrgPds.invalidate();
                },
            },
        );
    };

    const categoryOptions = useMemo(() => {
        const merged = new Set(categories ?? []);
        const selectedCategory = currentCategory?.trim();
        if (selectedCategory) {
            merged.add(selectedCategory);
        }

        const typedCategory = categorySearch.trim();
        if (typedCategory) {
            merged.add(typedCategory);
        }

        return Array.from(merged)
            .sort((a, b) => a.localeCompare(b))
            .map((category) => ({ label: category, value: category }));
    }, [categories, currentCategory, categorySearch]);
    const insurerOptions = (insurers ?? []).map((i) => ({ label: i.insurerName, value: i.insurerName }));

    const sections = orgPds?.Sections ?? [];

    if (isLoading) {
        return (
            <div className="space-y-6">
                <Skeleton active paragraph={{ rows: 8 }} />
            </div>
        );
    }

    return (
        <TRPCErrorBoundary error={error}>
            <Form<EditFormValues> layout="vertical" form={form} onFinish={handleSave} id={formId} className="space-y-6">
                {/* Header actions */}
                <PageLayout.Header
                    rightAddon={
                        orgPds ? (
                            <Space>
                                {orgPds.status !== 'PROCESSING' && orgPds.fileKey && (
                                    <Button type="secondary" icon={<RefreshCw />} onClick={handleReprocess}>
                                        Reprocess
                                    </Button>
                                )}
                                <Button type="secondary" icon={<Replace />} onClick={() => setReplaceFileOpen(true)}>
                                    Replace file
                                </Button>
                            </Space>
                        ) : undefined
                    }
                    primaryActionAddon={
                        <Button type="primary" htmlType="submit" form={formId} loading={isSaving}>
                            Save changes
                        </Button>
                    }
                />

                {/* Processing alert */}
                {orgPds?.status === 'PROCESSING' && (
                    <Alert
                        type="info"
                        showIcon
                        title="This PDS is currently being processed. The page will automatically refresh when processing completes."
                    />
                )}

                {/* Processing error alert */}
                {orgPds?.processingError && (
                    <Alert type="error" showIcon title="Processing error" description={orgPds.processingError} />
                )}

                <Tabs
                    defaultActiveKey="details"
                    size="large"
                    items={[
                        {
                            key: 'details',
                            label: 'Details',
                            children: (
                                <div className="space-y-6">
                                    {/* Metadata */}
                                    <Card title="Metadata">
                                        <div className="grid gap-6 md:grid-cols-2">
                                            <Form.Item<EditFormValues>
                                                label="PDS name"
                                                name="name"
                                                rules={[{ required: true, message: 'Please enter a PDS name' }]}
                                                className="md:col-span-2"
                                            >
                                                <Input
                                                    maxLength={255}
                                                    placeholder="e.g., Business Insurance PDS 2024"
                                                />
                                            </Form.Item>

                                            <Form.Item<EditFormValues>
                                                label="Insurer name"
                                                name="insurerName"
                                                rules={[{ required: true, message: 'Please enter an insurer name' }]}
                                            >
                                                <Select
                                                    showSearch
                                                    allowClear
                                                    placeholder="Select or type insurer name"
                                                    options={insurerOptions}
                                                    filterOption={(input, option) =>
                                                        (option?.label ?? '')
                                                            .toLowerCase()
                                                            .includes(input.toLowerCase())
                                                    }
                                                />
                                            </Form.Item>

                                            <Form.Item<EditFormValues>
                                                label="Category"
                                                name="category"
                                                rules={[{ required: true, message: 'Please enter a category' }]}
                                            >
                                                <Select
                                                    showSearch
                                                    allowClear
                                                    placeholder="Select or type category"
                                                    options={categoryOptions}
                                                    searchValue={categorySearch}
                                                    onSearch={setCategorySearch}
                                                    onSelect={() => setCategorySearch('')}
                                                    onBlur={() => {
                                                        const typedCategory = categorySearch.trim();
                                                        if (typedCategory) {
                                                            form.setFieldValue('category', typedCategory);
                                                        }
                                                        setCategorySearch('');
                                                    }}
                                                    onInputKeyDown={(event) => {
                                                        if (event.key !== 'Enter') return;
                                                        const typedCategory = categorySearch.trim();
                                                        if (!typedCategory) return;
                                                        event.preventDefault();
                                                        form.setFieldValue('category', typedCategory);
                                                        setCategorySearch('');
                                                    }}
                                                    filterOption={(input, option) =>
                                                        (option?.label ?? '')
                                                            .toLowerCase()
                                                            .includes(input.toLowerCase())
                                                    }
                                                />
                                            </Form.Item>

                                            <div className="flex gap-4">
                                                <Form.Item<EditFormValues>
                                                    label="Year"
                                                    name="year"
                                                    rules={[{ required: true, message: 'Please enter a year' }]}
                                                    className="w-32"
                                                >
                                                    <InputNumber
                                                        min={2000}
                                                        max={2100}
                                                        placeholder="2024"
                                                        className="w-full"
                                                    />
                                                </Form.Item>

                                                <Form.Item<EditFormValues>
                                                    label="Version"
                                                    name="version"
                                                    className="w-32"
                                                >
                                                    <Input placeholder="e.g., v2.1" />
                                                </Form.Item>
                                            </div>

                                            <Form.Item<EditFormValues> label="Effective date" name="effectiveDate">
                                                <DatePicker className="w-full" />
                                            </Form.Item>
                                        </div>
                                    </Card>

                                    {/* File information */}
                                    <Card title="File information">
                                        {orgPds?.fileKey ? (
                                            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
                                                <Descriptions.Item label="File name">
                                                    {orgPds.fileName ?? '-'}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Processing status">
                                                    <Tag
                                                        color={STATUS_COLORS[orgPds.status] ?? 'default'}
                                                        variant="outlined"
                                                    >
                                                        {orgPds.status}
                                                    </Tag>
                                                </Descriptions.Item>
                                                {orgPds.processingError && (
                                                    <Descriptions.Item label="Processing error" span={2}>
                                                        <span className="text-status-danger">
                                                            {orgPds.processingError}
                                                        </span>
                                                    </Descriptions.Item>
                                                )}
                                                <Descriptions.Item label="Created">
                                                    {formatDateTime(orgPds.createdAt)}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Last updated">
                                                    {formatDateTime(orgPds.updatedAt)}
                                                </Descriptions.Item>
                                            </Descriptions>
                                        ) : (
                                            <p className="text-muted-foreground">No file uploaded</p>
                                        )}
                                    </Card>
                                </div>
                            ),
                        },
                        {
                            key: 'analysis',
                            label: 'AI analysis',
                            children: <PdsAnalysisTab processedData={orgPds?.processedData} />,
                        },
                        {
                            key: 'sections',
                            label: `Sections (${sections.length})`,
                            children: (
                                <Table<OrgPdsSection>
                                    dataSource={sections}
                                    rowKey="id"
                                    size="small"
                                    pagination={false}
                                    locale={{
                                        emptyText: 'No sections — upload and process a PDF to extract sections',
                                    }}
                                    expandable={{
                                        expandedRowRender: (record) => (
                                            <p className="whitespace-pre-wrap text-muted-foreground text-sm">
                                                {record.content}
                                            </p>
                                        ),
                                    }}
                                    columns={[
                                        {
                                            title: 'Type',
                                            dataIndex: 'type',
                                            key: 'type',
                                            width: 120,
                                            render: (type: string) => (
                                                <Tag color={SECTION_TYPE_COLORS[type] ?? 'default'} variant="outlined">
                                                    {type}
                                                </Tag>
                                            ),
                                        },
                                        {
                                            title: 'Title',
                                            dataIndex: 'title',
                                            key: 'title',
                                            ellipsis: true,
                                        },
                                        {
                                            title: 'Subcategory',
                                            dataIndex: 'subcategory',
                                            key: 'subcategory',
                                            ellipsis: true,
                                            render: (value: string | null) => value ?? '-',
                                        },
                                        {
                                            title: 'Summary',
                                            dataIndex: 'summary',
                                            key: 'summary',
                                            ellipsis: true,
                                            width: 300,
                                        },
                                        {
                                            title: 'Key terms',
                                            dataIndex: 'keyTerms',
                                            key: 'keyTerms',
                                            width: 200,
                                            render: (terms: string[]) =>
                                                terms.length > 0 ? (
                                                    <Space size="micro" wrap>
                                                        {terms.slice(0, 3).map((term) => (
                                                            <Tag key={term} variant="outlined">
                                                                {term}
                                                            </Tag>
                                                        ))}
                                                        {terms.length > 3 && (
                                                            <span className="text-muted-foreground text-xs">
                                                                +{terms.length - 3}
                                                            </span>
                                                        )}
                                                    </Space>
                                                ) : (
                                                    '-'
                                                ),
                                        },
                                    ]}
                                />
                            ),
                        },
                        {
                            key: 'raw-text',
                            label: 'Raw text',
                            children: orgPds?.rawText ? (
                                <div className="max-h-[70vh] overflow-auto rounded border border-separator bg-card p-4">
                                    <Markdown variant="document">{orgPds.rawText}</Markdown>
                                </div>
                            ) : (
                                <Empty description="No raw text available. Process a PDF to extract text." />
                            ),
                        },
                    ]}
                />
            </Form>

            {/* Replace file modal */}
            {orgPds && (
                <SmartPdsUploadModal
                    open={replaceFileOpen}
                    onClose={() => setReplaceFileOpen(false)}
                    existingPdsId={orgPds.id}
                    onSuccess={() => {
                        setReplaceFileOpen(false);
                        trpcUtils.marketPds.getOrgPds.invalidate({ id: pdsId });
                        trpcUtils.marketPds.listOrgPds.invalidate();
                        trpcUtils.marketPds.listCategories.invalidate();
                        trpcUtils.marketPds.listInsurers.invalidate();
                    }}
                    mode="user"
                />
            )}
        </TRPCErrorBoundary>
    );
}
